import { useEffect, useMemo } from "react";
import { IColumnProps } from "devextreme-react/data-grid";
import { IItemProps } from "devextreme-react/form";

import { PageProps, UpdateDomTitleForForm } from "@ezhou/core";

import { mappingDimensionTypeOptions } from "../../model/constants/enums";
import { getUserProfileFromCache } from "../../services/userSettingService";

import { InactivatableCrudDataGrid } from "../common/InactivatableCrudDataGrid";
import { createFormulaEditCellComponent } from "../../components/formulaEditor";
import { FormulaContextKeys } from "../../model/formula";

import { dummyMappingDimensionRow, mappingDimensionColumns } from "./model/metaData";
import { MappingDimensionValues } from "./MappingDimensionValues";

const FORMULA_FIELDS = ["valueFormula", "defaultValueFormula", "alternativeValueFormula"];

const MappingDimensions = (pageProps: PageProps) => {
  const logger = pageProps.loggerProvider.getLogger("MappingDimensions");
  logger.debug("Rendering MappingDimensions page");

  useEffect(() => {
    UpdateDomTitleForForm(pageProps);
  });

  const httpClient = pageProps.appUtil.httpClients["eAccountingService"];

  // Render the formula editor through the column's `editCellComponent` so the DataGrid's editing
  // controller supplies the current value and receives changes. A form-item template would bypass
  // that binding and open with an empty editor for existing rows.
  const formulaEditCellComponent = useMemo(
    () =>
      createFormulaEditCellComponent({
        httpClient,
        contextKey: FormulaContextKeys.TessituraContext,
        rootObjects: ["TransactionInfo"],
      }),
    [httpClient],
  );

  const columnConfigs = useMemo(() => {
    const configs = new Map<string, IColumnProps>(mappingDimensionColumns);
    FORMULA_FIELDS.forEach((field) => {
      const existing = configs.get(field) ?? { dataField: field };
      configs.set(field, { ...existing, editCellComponent: formulaEditCellComponent });
    });
    return configs;
  }, [formulaEditCellComponent]);

  const editorItemOptions = useMemo(() => {
    const options = new Map<string, IItemProps>([
      [
        "active",
        {
          editorType: "dxCheckBox",
        },
      ],
      [
        "mappingDimensionType",
        {
          editorType: "dxSelectBox",
          editorOptions: {
            items: mappingDimensionTypeOptions,
          },
        },
      ],
    ]);
    return options;
  }, []);

  const organizationId = getUserProfileFromCache(pageProps.dataStore).defaultOrganization;
  const detailComponent = (props: any) => {
    return (
      <MappingDimensionValues
        parentKey={{
          organizationId: organizationId,
          mappingDimensionName: props.data.data.mappingDimensionName,
        }}
        pageProps={pageProps}
        logger={logger}
      />
    );
  };

  return (
    <InactivatableCrudDataGrid
      pageProps={pageProps}
      logger={logger}
      dummyRow={dummyMappingDimensionRow}
      baseUrl="mapping-dimensions"
      isOrganizationData={true}
      keyExpr="mappingDimensionName"
      formTitle="Mapping Dimension"
      formColCount={2}
      formColSpan={2}
      allowAdding={true}
      allowUpdating={true}
      allowDeleting={true}
      columnConfigs={columnConfigs}
      masterDetailComponent={detailComponent}
      editorItemOptions={editorItemOptions}
    ></InactivatableCrudDataGrid>
  );
};

export default MappingDimensions;
